import { useEffect, useState } from 'react'

import { isMobile } from '@utils/domUtils'

const isKeyboardInput = (elem: HTMLElement) =>
  (elem.tagName === 'INPUT' &&
    !['button', 'submit', 'checkbox', 'file', 'image'].includes((elem as HTMLInputElement).type)) ||
  elem.tagName === 'TEXTAREA' ||
  elem.hasAttribute('contenteditable')

export const useIsOnScreenKeyboardOpen = (containerRef: React.RefObject<HTMLDivElement | null>) => {
  const [isOpen, setOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const checkActiveElement = () => {
      const active = document.activeElement as HTMLElement | null
      if (!active) {
        setOpen(false)
        return
      }

      if (containerRef?.current && !containerRef.current.contains(active)) {
        setOpen(false)
        return
      }

      if (!isMobile()) {
        setOpen(false)
        return
      }

      setOpen(isKeyboardInput(active))
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return

      if (containerRef?.current && !containerRef.current.contains(target)) {
        return
      }

      if (!isMobile()) return

      if (isKeyboardInput(target)) {
        setOpen(true)
      }
    }

    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return

      if (containerRef?.current && !containerRef.current.contains(target)) {
        return
      }

      if (!isMobile()) return

      const next =
        (e.relatedTarget as HTMLElement | null) || (document.activeElement as HTMLElement | null)

      if (next && containerRef?.current?.contains(next) && isKeyboardInput(next)) {
        return
      }

      setOpen(false)
    }

    document.addEventListener('focusout', handleFocusOut)

    checkActiveElement()

    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
    }
  }, [containerRef])

  return isOpen
}
